import { Badge, Box, Button, ButtonBase, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { CustomSentenceApi, ICustomSentence } from "../api/CustomSentenceApi";
import { AudioApi } from "../api/AudioApi";
import { ChineseTeacher } from "../api/UserApi";
import AddIcon from "../asset/icon-add-black.png";
import ChatIcon from "../asset/icon-chat.png";
import DeleteIcon from "../asset/icon-delete.png";
import { handleErrorMessage } from '../component/ErrorDialog';
import AppBarContext from '../context/AppBarContext';
import CustomSentenceListContext from "../context/CustomSentenceListContext";
import { BLUE, BORDER, ERROR_TEXT, NAVY, PAGE_BACKGROUND, POPPINS, SLATE, SOFT_BLUE, SURFACE_MUTED, WARNING_SOFT, WARNING_TEXT } from "../style/verbaScholaPalette";

interface CustomSentenceMenuProps {
    isMenuCollapsed: boolean;
    onToggleMenuCollapse: () => void;
    isMobile: boolean;
    isTouchDevice: boolean;
    redesigned?: boolean;
}

function selectedVoiceIsPlayable(sentence: ICustomSentence, teacher: ChineseTeacher): boolean {
    return sentence.playable_teacher_voices !== undefined
        ? sentence.playable_teacher_voices.includes(teacher)
        : !sentence.processing_error && (sentence.processing_queue ?? 0) <= 0;
}

export default function CustomSentenceMenu({ isMenuCollapsed, onToggleMenuCollapse, isMobile, isTouchDevice, redesigned = false }: CustomSentenceMenuProps) {
    const { setErrorMessage } = React.useContext(AppBarContext);
    const history = useHistory();
    const location = useLocation();

    const [isLoading, setIsLoading] = React.useState(true);
    const [showSkeleton] = React.useState(false);

    const { customSentenceList, selectedSentence, setSelectedSentence, refreshCustomSentenceList } = React.useContext(CustomSentenceListContext);

    const [openDialog, setOpenDialog] = React.useState(false);
    const [deleteSentence, setDeleteSentence] = React.useState<ICustomSentence | null>(null);
    const [teacher, setTeacher] = React.useState(() => AudioApi.selectedCustomSentenceTeacher());

    React.useEffect(() => {
        return AudioApi.subscribeCustomSentenceTeacher(() => {
            setTeacher(AudioApi.selectedCustomSentenceTeacher());
            refreshCustomSentenceList();
        });
    }, [refreshCustomSentenceList]);

    React.useEffect(() => {
        setIsLoading(true);
        refreshCustomSentenceList();
        setIsLoading(false);
    }, [refreshCustomSentenceList]);

    const handleDelete = async (id: number) => {
        CustomSentenceApi.deleteBySentenceId(id)
            .then((response) => {
                if (response.status < 300) return response.data;
                console.error(response);
                handleErrorMessage(response, setErrorMessage, history, location);
                return [];
            })
            .then(() => {
                // CustomSentenceApi.getAll().then((response) => setCustomSentenceList(response.data));
                refreshCustomSentenceList();
            })
            .catch((err) => {
                if (err.response && err.response.status === 492) {
                    setErrorMessage("This user is read only and cannot delete.");
                } else {
                    handleErrorMessage(err, setErrorMessage, history, location);
                }
            });
    };

    const handleListSelect = (sentence: ICustomSentence) => {
        setSelectedSentence(sentence);
        if (isMobile || isTouchDevice) onToggleMenuCollapse();
    };

    const handleAddSentence = () => {
        setSelectedSentence({ id: -1, text: "AddSentence", pinyin: [] });
        if (isMobile || isTouchDevice) onToggleMenuCollapse();
    };

    const handleOpenDialog = (sentence: ICustomSentence) => {
        setDeleteSentence(sentence);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => setOpenDialog(false);

    const confirmDelete = async () => {
        if (deleteSentence !== null) {
            await handleDelete(deleteSentence.id);
            handleCloseDialog();
        }
    };

    const getChatGroupTitle = (chatDate: Date) => {
        const today = new Date();
        const yesterday = new Date();
        yesterday.setDate(today.getDate() - 1);

        if (
            chatDate.getFullYear() === today.getFullYear() &&
            chatDate.getMonth() === today.getMonth() &&
            chatDate.getDate() === today.getDate()
        ) {
            return { main: "Today", subtitle: chatDate.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }) };
        } else if (
            chatDate.getFullYear() === yesterday.getFullYear() &&
            chatDate.getMonth() === yesterday.getMonth() &&
            chatDate.getDate() === yesterday.getDate()
        ) {
            return { main: "Yesterday", subtitle: chatDate.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }) };
        } else {
            return { main: "Older", subtitle: chatDate.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }) };
        }
    };

    const groupSentencesByDate = (chats: ICustomSentence[]) => {
        // Sort all chats by latest_chat_date DESC
        const sortedChats = [...chats]
            .filter(chat => chat.latest_chat_date)
            .sort((a, b) =>
                new Date(b.latest_chat_date!).getTime() - new Date(a.latest_chat_date!).getTime()
            );

        const groups: { [key: string]: ICustomSentence[] } = {};

        // Group after sorting
        sortedChats.forEach(chat => {
            const chatDate = new Date(chat.latest_chat_date || new Date());
            const { main } = getChatGroupTitle(chatDate);

            if (!groups[main]) groups[main] = [];
            groups[main].push(chat);
        });

        // Fixed group order
        const order = ["Today", "Yesterday", "Older"];
        return order
            .filter(key => groups[key])
            .map(key => ({ main: key, sentences: groups[key] }));
    };

    if (isMenuCollapsed) return <></>;

    if (redesigned) return <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 2.25, borderBottom: `1px solid ${BORDER}`, flexShrink: 0 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography component="h2" sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 16, color: NAVY }}>Prior sentences</Typography>
                <Typography sx={{ fontSize: 12, color: SLATE }}>{customSentenceList?.length ?? 0} {customSentenceList?.length === 1 ? 'sentence' : 'sentences'}</Typography>
            </Box>
            <IconButton aria-label="Add a new sentence" onClick={handleAddSentence} sx={{ width: 44, height: 44, borderRadius: '12px', color: BLUE, backgroundColor: SOFT_BLUE, '&:hover': { backgroundColor: SURFACE_MUTED } }}>
                <AddRoundedIcon />
            </IconButton>
        </Stack>
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 1.25 }}>
            {groupSentencesByDate(customSentenceList || []).map(group => <Box key={group.main} sx={{ mb: 1.5 }}>
                <Typography sx={{ px: 1.25, py: 0.75, fontWeight: 700, fontSize: 11, letterSpacing: '.07em', color: SLATE, textTransform: 'uppercase' }}>{group.main}</Typography>
                <Stack spacing={0.5}>{group.sentences.map(sentence => <Stack key={sentence.id} direction="row" alignItems="center" sx={{ borderRadius: '13px', backgroundColor: selectedSentence.id === sentence.id ? SOFT_BLUE : 'transparent', '&:hover': { backgroundColor: selectedSentence.id === sentence.id ? SOFT_BLUE : SURFACE_MUTED } }}>
                    <ButtonBase onClick={() => handleListSelect(sentence)} sx={{ flex: 1, minWidth: 0, minHeight: 54, p: 1.25, justifyContent: 'flex-start', textAlign: 'left', borderRadius: '13px' }}>
                        <ChatBubbleOutlineRoundedIcon sx={{ color: BLUE, fontSize: 19, mr: 1, flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0 }}>
                            <Typography noWrap sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 13.5, color: NAVY }}>{sentence.text}</Typography>
                            <Typography noWrap sx={{ fontSize: 11.5, color: SLATE }}>{selectedVoiceIsPlayable(sentence, teacher)
                                ? (sentence.processing_queue ?? 0) > 0 ? 'Ready to practice · other voices preparing'
                                    : sentence.latest_chat_date ? new Date(sentence.latest_chat_date).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Ready to practice'
                                : sentence.processing_error ? 'Audio preparation failed'
                                    : sentence.audio_request_status?.[teacher]?.state === 'retrying' ? 'Audio delayed · retry scheduled'
                                    : (sentence.processing_queue ?? 0) > 0 ? `Preparing teacher audio · position ${sentence.processing_queue}`
                                        : 'Preparing teacher audio'}</Typography>
                        </Box>
                    </ButtonBase>
                    <IconButton aria-label={`Delete ${sentence.text}`} onClick={() => handleOpenDialog(sentence)} sx={{ width: 44, height: 44, color: SLATE, mr: 0.25 }}><DeleteOutlineRoundedIcon fontSize="small" /></IconButton>
                </Stack>)}</Stack>
            </Box>)}
            {!customSentenceList?.length && <Typography sx={{ px: 1.25, py: 2, fontSize: 13, color: SLATE }}>No sentences yet. Add one to start practicing.</Typography>}
        </Box>
        <Dialog open={openDialog} onClose={handleCloseDialog} aria-labelledby="pronunciation-delete-title">
            <DialogTitle id="pronunciation-delete-title" sx={{ fontFamily: POPPINS, color: NAVY }}>Delete sentence?</DialogTitle>
            <DialogContent><DialogContentText sx={{ color: SLATE }}>This removes the sentence and its saved recordings from your practice list.<br />{deleteSentence?.text}</DialogContentText></DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={handleCloseDialog}>Cancel</Button>
                <Button onClick={confirmDelete} variant="contained" sx={{ backgroundColor: ERROR_TEXT, color: '#fff', '&:hover': { backgroundColor: ERROR_TEXT } }}>Delete</Button>
            </DialogActions>
        </Dialog>
    </Box>;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box display="flex" sx={{ pt: 2, pl: 2, flexShrink: 0 }}>
                <Typography variant="h2" color="secondary">Prior Sentences</Typography>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                <List sx={{ flexGrow: 1, overflowY: "auto", minHeight: 0 }}>
                    {isLoading && showSkeleton
                        ? Array.from({ length: 5 }).map((_, i) => (
                            <Box key={i} sx={{ display: "flex", mb: 4.5, mt: 2, alignItems: "center" }}>
                                <Skeleton variant="rectangular" height={24} width={24} sx={{ borderRadius: 1, backgroundColor: redesigned ? PAGE_BACKGROUND : "#F2F7FF" }} />
                                <Skeleton variant="rectangular" height={20} width={256} sx={{ borderRadius: 1, backgroundColor: redesigned ? PAGE_BACKGROUND : "#F2F7FF", ml: 4 }} />
                            </Box>
                        ))
                        : groupSentencesByDate(customSentenceList || []).map(group => (
                             <Box key={group.main} sx={{ mb: 2, px: 2 }}>
                                    {/* Main header */}
                                    <Typography variant="h4" sx={{ color: "#fff", fontWeight: 600, mb: 1 }}>
                                        {group.main}
                                    </Typography>

                                {/* Sentences */}
                                {group.sentences.map(sentence => (
                                    <ListItem
                                        key={sentence.id}
                                        sx={{ p: 0, my: 2, width: "100%", maxWidth: "600px" }}
                                        secondaryAction={
                                            <div onClick={() => handleOpenDialog(sentence)}>
                                                <IconButton edge="end" aria-label="delete">
                                                    <img src={DeleteIcon} alt="Delete" style={{ width: "24px", height: "24px" }} />
                                                </IconButton>
                                            </div>
                                        }
                                    >
                                        <ListItemButton sx={{ p: 0 }} onClick={() => handleListSelect(sentence)} >
                                            <ListItemIcon>
                                                <img src={ChatIcon} alt="Chat" style={{ width: "24px", height: "24px" }} />
                                            </ListItemIcon>

                                            <ListItemText
                                                primary={
                                                    <Typography sx={{ color: "common.white", display: "flex", alignItems: "center", }} >
                                                        <span>{sentence.text}</span>

                                                        {!selectedVoiceIsPlayable(sentence, teacher) && sentence.audio_request_status?.[teacher]?.state !== 'retrying'
                                                            && sentence.processing_queue && sentence.processing_queue > 0 && (
                                                            <Tooltip title={`Sentence ${sentence.processing_queue} in queue`} >
                                                                <Badge
                                                                    badgeContent={ sentence.processing_queue }
                                                                    max={999}
                                                                    sx={{ ml: 3, ".MuiBadge-badge": { backgroundColor: redesigned ? WARNING_SOFT : "#FFE299", color: redesigned ? WARNING_TEXT : "#2A2459", }, }}
                                                                >
                                                                    <span />
                                                                </Badge>
                                                            </Tooltip>
                                                        )}
                                                    </Typography>
                                                }

                                                secondary={
                                                    <Typography sx={{ color: redesigned ? SLATE : "#A0A0A0", fontSize: 12, }} >
                                                        {!selectedVoiceIsPlayable(sentence, teacher) && sentence.processing_error ? 'Audio preparation failed'
                                                            : !selectedVoiceIsPlayable(sentence, teacher) && sentence.audio_request_status?.[teacher]?.state === 'retrying' ? 'Audio delayed · retry scheduled'
                                                            : selectedVoiceIsPlayable(sentence, teacher) && (sentence.processing_queue ?? 0) > 0 ? 'Ready to practice · other voices preparing'
                                                            : sentence.latest_chat_date
                                                            ? `${new Date(sentence.latest_chat_date).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", })} 
                                                                ${new Date(sentence.latest_chat_date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", })}`
                                                            : ( 
                                                                <Skeleton variant="text" width={120} />
                                                            )}
                                                    </Typography>
                                                }
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </Box>
                        ))
                    }
                    </List>
                </Box>

            <Box sx={{ flexShrink: 0 }}>
                <Button variant="contained" sx={{ width: "100%", p: "12px", mb: 2, mt: 2 }} className="secondaryButton" onClick={handleAddSentence}>
                    <img src={AddIcon} alt="Add" style={{ width: "24px", height: "24px", marginRight: "16px" }} />
                    Add a new sentence
                </Button>
            </Box>

            <Dialog open={openDialog} onClose={handleCloseDialog} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
                <DialogTitle id="alert-dialog-title">
                    <Typography variant="h4" sx={{ color: redesigned ? NAVY : "#383838" }}>Delete Sentence?</Typography>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: redesigned ? SLATE : "#8D8D8D" }}>
                        Are you sure you want to delete this sentence?<br />
                        {deleteSentence && deleteSentence.text}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "space-between", "& .MuiButton-root": { flex: 1 } }}>
                    <Button onClick={handleCloseDialog} sx={{ color: redesigned ? SLATE : "#787878" }}>Cancel</Button>
                    <Button onClick={confirmDelete} autoFocus variant="contained" color="error" sx={{ color: "#fff7f7" }}>Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
